import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'gatsby'
import { useDevTools } from 'global/context/sub-contexts/devToolsContext'

import * as styles from './DevToolsPanel.module.scss'

const PANEL_WIDTH = 280
const PANEL_HEIGHT_ESTIMATE = 160
const MARGIN = 16

const getInitialPosition = () => {
  if (typeof window === 'undefined') return { x: 0, y: 0 }
  return {
    x: window.innerWidth - PANEL_WIDTH - MARGIN,
    y: MARGIN + 60,
  }
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const DevToolsPanel = () => {
  const {
    panelOpen,
    closePanel,
    customCssDisabled,
    toggleCustomCssDisabled,
  } = useDevTools()

  const [position, setPosition] = useState(getInitialPosition)
  const dragRef = useRef(null)
  const dragStart = useRef(null)

  useEffect(() => {
    if (panelOpen) {
      setPosition(prev => {
        if (typeof window === 'undefined') return prev
        const maxX = window.innerWidth - PANEL_WIDTH - MARGIN
        const maxY = window.innerHeight - PANEL_HEIGHT_ESTIMATE - MARGIN
        return {
          x: clamp(prev.x, MARGIN, Math.max(MARGIN, maxX)),
          y: clamp(prev.y, MARGIN, Math.max(MARGIN, maxY)),
        }
      })
    }
  }, [panelOpen])

  const onPointerDown = useCallback(
    e => {
      if (e.button !== 0) return
      dragStart.current = {
        pointerId: e.pointerId,
        offsetX: e.clientX - position.x,
        offsetY: e.clientY - position.y,
      }
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [position.x, position.y],
  )

  const onPointerMove = useCallback(e => {
    if (!dragStart.current || dragStart.current.pointerId !== e.pointerId)
      return
    const nextX = e.clientX - dragStart.current.offsetX
    const nextY = e.clientY - dragStart.current.offsetY
    const maxX = window.innerWidth - PANEL_WIDTH
    const maxY = window.innerHeight - 40
    setPosition({
      x: clamp(nextX, 0, Math.max(0, maxX)),
      y: clamp(nextY, 0, Math.max(0, maxY)),
    })
  }, [])

  const onPointerUp = useCallback(e => {
    if (!dragStart.current || dragStart.current.pointerId !== e.pointerId)
      return
    dragStart.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }, [])

  if (!panelOpen) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={dragRef}
      className={styles.panel}
      style={{ left: position.x, top: position.y, width: PANEL_WIDTH }}
      role="dialog"
      aria-label="Dev Tools"
    >
      <div
        className={styles.header}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <span className={styles.title}>Dev Tools</span>
        <button
          type="button"
          className={styles.close}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation()
            closePanel()
          }}
          aria-label="Close dev tools"
        >
          ×
        </button>
      </div>
      <div className={styles.body}>
        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <strong>Custom CSS</strong>
            <small>
              Disable to compare layout against page&apos;s injected styles.
            </small>
          </div>
          <button
            type="button"
            className={`${styles.toggle} ${customCssDisabled ? styles.toggleOff : styles.toggleOn}`}
            onClick={toggleCustomCssDisabled}
            aria-pressed={!customCssDisabled}
          >
            {customCssDisabled ? 'OFF' : 'ON'}
          </button>
        </div>
        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <strong>API URL</strong>
            <small>Point this browser at a different API instance.</small>
          </div>
          <Link to="/api-url" className={styles.link}>
            Open
          </Link>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default DevToolsPanel
